import { Fragment, useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { parseMarkdown, storagePublicPrefix, type BlockNode, type InlineNode } from "@/lib/safe-markdown";

const IMAGE_PREFIX = storagePublicPrefix(import.meta.env["VITE_SUPABASE_URL"] as string | undefined);

function Inline({ nodes }: { nodes: readonly InlineNode[] }): JSX.Element {
  return (
    <>
      {nodes.map((node, i) => {
        switch (node.type) {
          case "text":
            return <Fragment key={i}>{node.value}</Fragment>;
          case "strong":
            return <strong key={i} className="font-semibold text-foreground"><Inline nodes={node.children} /></strong>;
          case "em":
            return <em key={i}><Inline nodes={node.children} /></em>;
          case "code":
            return <code key={i} className="rounded bg-[hsl(var(--surface-raised))] px-1 py-0.5 font-mono text-[0.85em]">{node.value}</code>;
          case "link":
            return node.external ? (
              <a key={i} href={node.href} target="_blank" rel="noopener noreferrer nofollow" className="font-medium text-primary underline-offset-4 hover:underline">
                <Inline nodes={node.children} />
              </a>
            ) : (
              <Link key={i} to={node.href} className="font-medium text-primary underline-offset-4 hover:underline">
                <Inline nodes={node.children} />
              </Link>
            );
        }
      })}
    </>
  );
}

function Block({ block }: { block: BlockNode }): JSX.Element {
  switch (block.type) {
    case "heading": {
      const className = "font-semibold text-foreground";
      if (block.level === 1) return <h3 className={`${className} text-base`}><Inline nodes={block.children} /></h3>;
      return <h4 className={`${className} text-sm`}><Inline nodes={block.children} /></h4>;
    }
    case "paragraph":
      return <p><Inline nodes={block.children} /></p>;
    case "list": {
      const items = block.items.map((item, i) => <li key={i}><Inline nodes={item} /></li>);
      return block.ordered
        ? <ol className="list-decimal space-y-1 pl-5">{items}</ol>
        : <ul className="list-disc space-y-1 pl-5">{items}</ul>;
    }
    case "image":
      return <img src={block.src} alt={block.alt} loading="lazy" referrerPolicy="no-referrer" className="w-full rounded-lg border border-border" />;
    case "hr":
      return <hr className="border-border" />;
  }
}

/** Corpo rico seguro: markdown -> nos React (sem HTML cru, links e imagens filtrados). */
export function SafeMarkdown({ source }: { source: string }): JSX.Element {
  const blocks = useMemo(() => parseMarkdown(source, IMAGE_PREFIX), [source]);
  return (
    <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
      {blocks.map((block, i) => <Block key={i} block={block} />)}
    </div>
  );
}
